import { useState } from "react";
import { Link } from "react-router-dom";
import { StatCard } from "@/components/ui/StatCard";
import { Card } from "@/components/ui/Card";
import { LoadingState, ErrorState } from "@/components/ui/AsyncState";
import { LeadStatusBadge } from "@/components/ui/StatusBadge";
import { RangePicker, rangeQuery, type RangeSelection } from "@/components/ui/RangePicker";
import { useApi } from "@/lib/use-api";
import { formatCurrencyFromCents, formatPercent, formatRelativeTime, formatPhone } from "@/lib/format";
import type { AnalyticsSummary, Lead } from "@platform/shared";


export function DashboardHome() {
  const [selection, setSelection] = useState<RangeSelection>({ range: "30d" });
  const query = rangeQuery(selection);
  const summary = useApi<{ summary: AnalyticsSummary }>(`/api/analytics/summary?${query}`, [query]);
  const leads = useApi<{ leads: Lead[] }>("/api/leads");

  const recentLeads = (leads.data?.leads ?? []).slice(0, 5);

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl text-ink">Dashboard</h1>
          <p className="mt-1 text-sm text-ink-soft">How much business Callback has helped you capture.</p>
        </div>
        <RangePicker value={selection} onChange={setSelection} />
      </div>

      {summary.isLoading && <LoadingState label="Loading your metrics…" />}
      {summary.error && <ErrorState message={summary.error} onRetry={summary.refetch} />}

      {summary.data && (
        <>
          {/* Headline value card: CONFIRMED revenue only. Estimated value is
              shown separately (and labeled) so the two are never conflated. */}
          <Card className="bg-brand-700 px-6 py-6 text-white shadow-card">
            <p className="text-sm text-brand-100">
              Confirmed revenue · {summary.data.summary.startDate} to {summary.data.summary.endDate}
            </p>
            <p className="mt-1 font-display text-4xl">
              {formatCurrencyFromCents(summary.data.summary.confirmedRevenueCents)}
            </p>
            <p className="mt-1 text-sm text-brand-100">
              Plus{" "}
              <strong className="text-white">
                {formatCurrencyFromCents(summary.data.summary.estimatedLeadValueCents)}
              </strong>{" "}
              in <em>estimated</em> value from newly qualified leads (a projection, not money received).
            </p>
            <div className="mt-4 flex flex-wrap gap-x-8 gap-y-2 text-sm text-brand-100">
              <span>
                <strong className="text-white">{summary.data.summary.convertedLeads}</strong> leads won
              </span>
              <span>
                <strong className="text-white">{formatPercent(summary.data.summary.responseRate)}</strong> response
                rate
              </span>
              <span>
                <strong className="text-white">{formatPercent(summary.data.summary.qualificationRate)}</strong>{" "}
                qualification rate
              </span>
            </div>
          </Card>

          <div className="grid gap-4 sm:grid-cols-3">
            <StatCard label="Missed calls" value={String(summary.data.summary.missedCalls)} />
            <StatCard label="Calls answered" value={String(summary.data.summary.answeredCalls)} />
            <StatCard label="AI conversations" value={String(summary.data.summary.aiConversations)} />
            <StatCard label="Qualified leads" value={String(summary.data.summary.qualifiedLeads)} />
            <StatCard label="Won leads" value={String(summary.data.summary.convertedLeads)} />
            <StatCard
              label="Missed calls followed up"
              value={`${summary.data.summary.missedCallsFollowedUp} of ${summary.data.summary.missedCalls}`}
              hint="Missed calls that got a follow-up"
            />
          </div>
        </>
      )}

      <Card className="overflow-hidden">
        <div className="border-b border-line px-5 py-4">
          <p className="font-medium text-ink">Recent leads</p>
        </div>
        {leads.isLoading && <LoadingState />}
        {leads.error && (
          <div className="px-5 py-4">
            <ErrorState message={leads.error} onRetry={leads.refetch} />
          </div>
        )}
        {!leads.isLoading && !leads.error && recentLeads.length === 0 && (
          <div className="px-5 py-8 text-center text-sm text-ink-soft">
            No leads yet. Once Callback starts responding to missed calls, they'll show up here.
          </div>
        )}
        {recentLeads.length > 0 && (
          <table className="w-full text-left text-sm">
            <thead className="border-b border-line bg-paper text-ink-soft">
              <tr>
                <th className="px-5 py-2.5 font-medium">Customer</th>
                <th className="px-5 py-2.5 font-medium">Service</th>
                <th className="px-5 py-2.5 font-medium">Status</th>
                <th className="px-5 py-2.5 font-medium">Received</th>
              </tr>
            </thead>
            <tbody>
              {recentLeads.map((lead) => (
                <tr key={lead.id} className="border-b border-line last:border-0 hover:bg-paper">
                  <td className="px-5 py-3">
                    <Link to={`/dashboard/leads/${lead.id}`} className="font-medium text-ink hover:underline">
                      {lead.name ?? formatPhone(lead.phone)}
                    </Link>
                  </td>
                  <td className="px-5 py-3 text-ink-soft">{lead.service ?? "—"}</td>
                  <td className="px-5 py-3">
                    <LeadStatusBadge status={lead.status} />
                  </td>
                  <td className="px-5 py-3 text-ink-soft">{formatRelativeTime(lead.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

    </div>
  );
}
